import { api } from './api';
import type { UserResponse as UserWire } from '@envirolink/api-schema';

// The GET /users/ list endpoint returns a bare PaginatedResponse (items
// untyped in the spec — no response_model item type), so the envelope below
// asserts UserWire per item. TODO(response_model): type the users list
// endpoint server-side so this assertion becomes generated truth.
type PaginatedUsersWire = {
  items: UserWire[];
  total: number;
  page: number;
  size: number;
  pages: number;
};

// last_login_at is NOT declared on the backend UserResponse schema — the UI
// has always read it as an undeclared enrichment. Kept behavior-identical;
// TODO(response_model): add last_login_at to UserResponse server-side (or
// confirm it's stripped and drop the column from the UI).
const lastLoginAt = (user: UserWire): string | null | undefined =>
  (user as { last_login_at?: string | null }).last_login_at;

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  /** Extra roles (an inspector who also consults holds "consultant" here). */
  secondaryRoles: string[];
  organizationId: string | null;
  isActive: boolean;
  mfaEnabled: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  lastLoginAt?: string | null;
}

export interface PaginatedUsers {
  items: User[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

interface GetUsersParams {
  role?: string;
  isActive?: boolean;
  page?: number;
  size?: number;
  search?: string;
  includeLegacy?: boolean;
}

class UsersService {
  async getUsers(params: GetUsersParams = {}): Promise<PaginatedUsers> {
    const queryParams = new URLSearchParams();
    if (params.role) queryParams.append('role', params.role);
    if (params.isActive !== undefined)
      queryParams.append('is_active', String(params.isActive));
    if (params.page) queryParams.append('page', String(params.page));
    if (params.size) queryParams.append('size', String(params.size));
    if (params.search) queryParams.append('search', params.search);
    if (params.includeLegacy)
      queryParams.append('include_legacy', String(params.includeLegacy));

    const queryString = queryParams.toString();
    const url = `/users/${queryString ? `?${queryString}` : ''}`;

    const response = await api.get<PaginatedUsersWire>(url);

    return {
      items: response.data.items.map((user) => ({
        id: user.id,
        email: user.email ?? '',
        firstName: user.first_name,
        lastName: user.last_name,
        role: user.role,
        secondaryRoles: user.secondary_roles ?? [],
        organizationId: user.organization_id ?? null,
        isActive: user.is_active,
        mfaEnabled: user.mfa_enabled,
        createdAt: user.created_at,
        updatedAt: user.updated_at,
        lastLoginAt: lastLoginAt(user),
      })),
      total: response.data.total,
      page: response.data.page,
      size: response.data.size,
      pages: response.data.pages,
    };
  }

  async getConsultants(): Promise<User[]> {
    const consultants = await this.getUsers({
      role: 'consultant',
      isActive: true,
      size: 100,
    });
    const seniorConsultants = await this.getUsers({
      role: 'senior_consultant',
      isActive: true,
      size: 100,
    });
    return [...consultants.items, ...seniorConsultants.items];
  }

  async getInspectors(): Promise<User[]> {
    // Legacy-imported accounts hold most historical inspection activity, so
    // activity/payroll pickers must include them.
    const inspectors = await this.getUsers({
      role: 'inspector',
      isActive: true,
      size: 500,
      includeLegacy: true,
    });
    return inspectors.items.sort((a, b) =>
      `${a.lastName} ${a.firstName}`.localeCompare(
        `${b.lastName} ${b.firstName}`
      )
    );
  }

  async getUser(userId: string): Promise<User> {
    const response = await api.get<UserWire>(`/users/${userId}`);
    const user = response.data;
    return {
      id: user.id,
      email: user.email ?? '',
      firstName: user.first_name,
      lastName: user.last_name,
      role: user.role,
      secondaryRoles: user.secondary_roles ?? [],
      organizationId: user.organization_id ?? null,
      isActive: user.is_active,
      mfaEnabled: user.mfa_enabled,
      createdAt: user.created_at,
      updatedAt: user.updated_at,
      lastLoginAt: lastLoginAt(user),
    };
  }

  async updateUser(
    userId: string,
    data: Partial<{
      firstName: string;
      lastName: string;
      role: string;
      organizationId: string | null;
      isActive: boolean;
    }>
  ): Promise<User> {
    const apiData: any = {};
    if (data.firstName !== undefined) apiData.first_name = data.firstName;
    if (data.lastName !== undefined) apiData.last_name = data.lastName;
    if (data.role !== undefined) apiData.role = data.role;
    if (data.organizationId !== undefined)
      apiData.organization_id = data.organizationId;
    if (data.isActive !== undefined) apiData.is_active = data.isActive;

    const response = await api.patch<UserWire>(`/users/${userId}`, apiData);
    const user = response.data;
    return {
      id: user.id,
      email: user.email ?? '',
      firstName: user.first_name,
      lastName: user.last_name,
      role: user.role,
      secondaryRoles: user.secondary_roles ?? [],
      organizationId: user.organization_id ?? null,
      isActive: user.is_active,
      mfaEnabled: user.mfa_enabled,
      createdAt: user.created_at,
      updatedAt: user.updated_at,
    };
  }

  async createUser(data: {
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    password: string;
    organizationId?: string | null;
    phone?: string | null;
  }): Promise<User> {
    const response = await api.post<UserWire>('/users/', {
      email: data.email,
      first_name: data.firstName,
      last_name: data.lastName,
      role: data.role,
      password: data.password,
      organization_id: data.organizationId || undefined,
      phone: data.phone || undefined,
    });
    const user = response.data;
    return {
      id: user.id,
      email: user.email ?? '',
      firstName: user.first_name,
      lastName: user.last_name,
      role: user.role,
      secondaryRoles: user.secondary_roles ?? [],
      organizationId: user.organization_id ?? null,
      isActive: user.is_active,
      mfaEnabled: user.mfa_enabled,
      createdAt: user.created_at,
      updatedAt: user.updated_at,
    };
  }
}

export const usersService = new UsersService();
